import { useEffect, useRef, useState } from 'react';
import { storage } from '@/lib/storage';

/** `devlearn:code:<challengeId>:v<contentVersion>` (+ `:<file>` for extra editable files). */
export function codeKey(challengeId: string, contentVersion: number, file = 'main.js') {
  const base = `devlearn:code:${challengeId}:v${contentVersion}`;
  return file === 'main.js' ? base : `${base}:${file}`;
}

/** Saved code from an OLDER content version (kept when a challenge was updated), newest first. */
export function previousVersionCode(challengeId: string, contentVersion: number): string | null {
  const prefix = `devlearn:code:${challengeId}:v`;
  const versions = storage
    .keys(prefix)
    .map((k) => /^devlearn:code:[^:]+:v(\d+)$/.exec(k)?.[1])
    .filter((v): v is string => v !== undefined)
    .map(Number)
    .filter((v) => v < contentVersion)
    .sort((a, b) => b - a);
  return versions[0] !== undefined ? storage.get(codeKey(challengeId, versions[0])) : null;
}

export type SaveStatus = 'saved' | 'saving' | 'unsaved';

/**
 * Editor state backed by localStorage, debounced 500 ms. Starts from saved code if present. Learner code is never
 * lost: it is saved on change and on unload, and errors elsewhere never touch it.
 */
export function useAutosave(key: string, initial: string) {
  const [value, setValue] = useState(() => storage.get(key) ?? initial);
  const [status, setStatus] = useState<SaveStatus>('saved');
  const latest = useRef(value);
  useEffect(() => {
    latest.current = value;
  }, [value]);

  // Sync with localStorage (external system).
  useEffect(() => {
    if (status !== 'saving') return;
    const timer = setTimeout(() => {
      setStatus(storage.set(key, latest.current) ? 'saved' : 'unsaved');
    }, 500);
    return () => clearTimeout(timer);
  }, [key, value, status]);

  useEffect(() => {
    const flush = () => storage.set(key, latest.current);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [key]);

  function update(next: string) {
    setValue(next);
    setStatus('saving');
  }

  function reset() {
    storage.remove(key);
    setValue(initial);
    latest.current = initial;
    setStatus('saved');
  }

  return { value, setValue: update, reset, status };
}
